import React, { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { FileText, Layers, ImageIcon } from 'lucide-react';
import { Modal, Button } from '../common';
import { Category, SubcategoryHierarchy } from '../../types';
import { subcategoryService } from '@/services/api'; // ✅ importante

/* ============================================================
   Helpers de íconos
   ============================================================ */
const IconAvatar: React.FC<{
  url?: string | null;
  alt: string;
  fallback: React.ReactNode;
  size?: number;
  className?: string;
}> = ({ url, alt, fallback, size = 36, className }) => {
  const [errored, setErrored] = React.useState(false);
  const valid = typeof url === 'string' && url.trim().length > 0 && !errored;

  return (
    <div className={`inline-flex items-center gap-2 ${className || ''}`}>
      <div
        className="grid place-items-center rounded-md overflow-hidden border border-gray-200 bg-white"
        style={{ width: size, height: size }}
        aria-label={alt}
        title={alt}
      >
        {valid ? (
          <img
            src={url as string}
            alt={alt}
            className="w-full h-full object-contain"
            onError={() => setErrored(true)}
          />
        ) : (
          <div className="text-gray-500">{fallback}</div>
        )}
      </div>
    </div>
  );
};

const getCategoryIconUrl = (cat: any) => cat?.iconUrl ?? cat?.icon_url ?? null;
const getSubIconUrl = (sub: any) => sub?.iconUrl ?? sub?.icon_url ?? null;

/* ============================================================
   Tipos de formulario
   ============================================================ */
interface SubcategoryFormData {
  name: string;
  size: string;
  categoryId: string;
  parentId?: string;
  iconUrl?: string;
  iconFile?: FileList;
}

interface UpdateSubcategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  subcategory: SubcategoryHierarchy | null;
  categories: Category[];
  allSubcategories: Array<
    SubcategoryHierarchy & { categoryName: string; categoryId: string; level?: number }
  >;
  onUpdated: () => Promise<void>;
  isSubmitting: boolean;
}

/* ============================================================
   MODAL: Editar Subcategoría
   ============================================================ */
export const UpdateSubcategoryModal: React.FC<UpdateSubcategoryModalProps> = ({
  isOpen,
  onClose,
  subcategory,
  categories,
  allSubcategories,
  onUpdated,
  isSubmitting,
}) => {
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<SubcategoryFormData>();

  // 🧩 Prellenar los campos al abrir
  useEffect(() => {
    if (subcategory) {
      reset({
        name: subcategory.name,
        size: subcategory.size,
        categoryId: subcategory.categoryId,
        parentId: (subcategory as any).parentSubcategoryId || '',
        iconUrl: subcategory.iconUrl || '',
      });
      setSelectedCategoryId(subcategory.categoryId);
    }
  }, [subcategory, reset]);

  // Imagen preview
  const imageFileList = watch('iconFile');
  const selectedFile = imageFileList && imageFileList.length > 0 ? imageFileList[0] : null;
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (selectedFile) {
      if (selectedFile.size > 5 * 1024 * 1024) {
        alert('El archivo supera el límite de 5 MB. Selecciona una imagen más liviana.');
        setPreviewUrl(null);
        return;
      }
      const url = URL.createObjectURL(selectedFile);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreviewUrl(null);
  }, [selectedFile]);

  // Subcategorías padre disponibles
  const availableParents = useMemo(() => {
    if (!selectedCategoryId) return [];
    return allSubcategories.filter((sub) => sub.categoryId === selectedCategoryId);
  }, [selectedCategoryId, allSubcategories]);

  const parentId = watch('parentId');
  const selectedParent = useMemo(
    () => availableParents.find((p) => p.id === parentId),
    [availableParents, parentId]
  );

  const selectedCategory = useMemo(
    () => categories.find((c) => c.id === selectedCategoryId),
    [categories, selectedCategoryId]
  );

  // ============================================================
  // 📦 Submit handler
  // ============================================================
  const onSubmitForm = async (data: SubcategoryFormData) => {
    if (!subcategory) return;
    try {
      const file = data.iconFile?.[0];

      const submitData = {
        name: data.name,
        size: data.size,
        categoryId: data.categoryId,
        parentSubcategoryId: data.parentId || undefined,
        iconFile: file ?? undefined,
        iconUrl: file ? undefined : data.iconUrl || undefined,
      };

      // console.log('[UpdateSubcategoryModal] submitData:', submitData);

      // ✅ Si hay archivo -> PUT /api/upload/subcategory/:id
      // ✅ Si no hay archivo -> PUT /api/admin/subcategories/:id
      await subcategoryService.update(subcategory.id, submitData);
      await onUpdated();

      reset();
      setSelectedCategoryId('');
      onClose();
    } catch (error) {
      console.error('[UpdateSubcategoryModal] Error al actualizar:', error);
    }
  };

  const handleClose = () => {
    reset();
    setSelectedCategoryId('');
    onClose();
  };

  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategoryId(categoryId);
    setValue('categoryId', categoryId);
    setValue('parentId', '');
  };

  const renderIndentedOption = (sub: SubcategoryHierarchy & { level?: number }) => {
    const indent = '  '.repeat(sub.level || 0);
    return `${indent}${sub.name} (${sub.size})`;
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Editar Subcategoría" size="lg">
      <form onSubmit={handleSubmit(onSubmitForm)} className="space-y-6">
        {/* Categoría */}
        <div>
          <label className="block text-sm font-medium text-brinsa-azul mb-2">Categoría *</label>
          <div className="flex items-center justify-between gap-3">
            <select
              {...register('categoryId', { required: 'La categoría es requerida' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brinsa-azul"
              onChange={(e) => handleCategoryChange(e.target.value)}
              value={selectedCategoryId}
            >
              <option value="">Seleccionar categoría...</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
            <IconAvatar
              url={getCategoryIconUrl(selectedCategory)}
              alt={selectedCategory ? selectedCategory.name : 'Categoría'}
              fallback={<Layers className="w-5 h-5" />}
            />
          </div>
          {errors.categoryId && (
            <p className="mt-1 text-sm text-red-600">{errors.categoryId.message}</p>
          )}
        </div>

        {/* Subcategoría padre */}
        {availableParents.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-brinsa-azul mb-2">
              Subcategoría Padre (opcional)
            </label>
            <select
              {...register('parentId')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brinsa-azul"
            >
              <option value="">Nivel raíz</option>
              {availableParents.map((parent) => (
                <option key={parent.id} value={parent.id}>
                  {renderIndentedOption(parent)}
                </option>
              ))}
            </select>
            {parentId && selectedParent && (
              <div className="mt-2">
                <IconAvatar
                  url={getSubIconUrl(selectedParent as any)}
                  alt={selectedParent.name}
                  fallback={<FileText className="w-5 h-5" />}
                />
              </div>
            )}
          </div>
        )}

        {/* Nombre */}
        <div>
          <label className="block text-sm font-medium text-brinsa-azul mb-2">Nombre *</label>
          <input
            type="text"
            {...register('name', { required: 'El nombre es requerido' })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brinsa-azul"
          />
        </div>

        {/* Tamaño */}
        <div>
          <label className="block text-sm font-medium text-brinsa-azul mb-2">
            Tamaño / Presentación *
          </label>
          <input
            type="text"
            {...register('size', { required: 'El tamaño es requerido' })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brinsa-azul"
          />
        </div>

        {/* Ícono */}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-brinsa-azul mb-2">Ícono (archivo)</label>
            <input
              type="file"
              accept="image/*"
              {...register('iconFile')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brinsa-azul focus:border-transparent"
            />
            <p className="mt-1 text-xs text-gray-500">
              Tamaño máximo permitido: <span className="font-semibold text-brinsa-azul">5 MB</span>.
              Formatos recomendados: JPG o PNG.
            </p>

            {previewUrl && (
              <div className="mt-3">
                <div className="text-xs text-brinsa-azulOscuro/70 mb-1">Vista previa:</div>
                <div className="w-full h-36 rounded-lg border border-brinsa-celeste/30 bg-gray-50 flex items-center justify-center overflow-hidden">
                  <img
                    src={previewUrl}
                    alt="preview"
                    className="max-w-full max-h-full object-contain transition-transform duration-200 hover:scale-105"
                  />
                </div>
              </div>
            )}
          </div>

          {/* URL */}
          <div>
            <label className="block text-sm font-medium text-brinsa-azul mb-2">URL de Ícono</label>
            <input
              type="url"
              {...register('iconUrl')}
              disabled={!!selectedFile}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brinsa-azul focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
            />
            {selectedFile && (
              <p className="mt-1 text-xs text-brinsa-azulOscuro/70">
                Se usará la imagen subida. Si quieres usar una URL, elimina el archivo seleccionado.
              </p>
            )}
          </div>
        </div>

        {/* Botones */}
        <div className="flex justify-end space-x-3 pt-4">
          <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button
            type="submit"
            isLoading={isSubmitting}
            leftIcon={<ImageIcon className="w-4 h-4" />}
          >
            Guardar Cambios
          </Button>
        </div>
      </form>
    </Modal>
  );
};
